import { useEffect, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Divider } from '@mui/material';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL
  || process.env.REACT_APP_API_URL
  || process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const API_BASE = getBackendUrl();

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

// OpenEMR payment_method slugs (see s4-s3 server/lib/paymentPosting.js's
// mapGatewayToPaymentMethod) shown as a simple icon instead of the raw
// "creditcard_payment"/"cash_payment" string.
function paymentMethodIcon(method) {
  switch (String(method || '').toLowerCase()) {
    case 'creditcard_payment': return '💳';
    case 'cash_payment': return '💵';
    case 'check_payment': return '🧾';
    case 'carecredit_payment': return '💰';
    default: return method || '';
  }
}

function Message({ title, children, error }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', display: 'grid', placeItems: 'center', px: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 480, p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>{title}</Typography>
        <Typography color={error ? 'error' : 'text.secondary'}>{children}</Typography>
      </Paper>
    </Box>
  );
}

function Row({ label, sub, amount, bold, strong }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Box>
        <Typography fontWeight={bold ? 800 : 600} fontSize={bold ? 16 : 14}>{label}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
      {amount != null && (
        <Typography fontWeight={strong ? 800 : 700} fontSize={bold ? 16 : 14} whiteSpace="nowrap" color={strong ? 'primary' : 'inherit'}>
          {amount}
        </Typography>
      )}
    </Box>
  );
}

function Meta({ children }) {
  return <Typography color="text.secondary" variant="body2" sx={{ mt: 0.25 }}>{children}</Typography>;
}

// Public, read-only billing-document page — opened from a short link (e.g.
// strz.me/Ab3kLm9Px) sent to a patient via SMS/email from the mobile app's
// Bills actions. Fetches /api/shares/:token, which s4forms reverse-proxies
// to s4-s3 (see server/index.js's proxyToS4S3); the random token is the only
// credential, same model as the deposit-receipt/estimate/postop links.
export default function SharePage({ token }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/shares/${encodeURIComponent(token)}`, { cache: 'no-store' })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Unable to load this document.');
        setDoc(payload);
      })
      .catch((err) => setError(err.message || 'Unable to load this document.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!doc || !doc.success) return <Message title="Unable to Open Document" error={error}>{error || 'This link is unavailable.'}</Message>;

  if (doc.type === 'receipt') {
    const r = doc.receipt;
    const methodLine = [paymentMethodIcon(r.method), r.reference ? `Ref ${r.reference}` : null].filter(Boolean).join(' · ');
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', px: 2, py: { xs: 3, sm: 6 } }}>
        <Paper elevation={0} sx={{ maxWidth: 480, mx: 'auto', borderRadius: 3, overflow: 'hidden', border: '1px solid #dbe5f1' }}>
          <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: 3, py: 2.5 }}>
            <Typography variant="overline" sx={{ opacity: 0.85 }}>{r.practiceName}</Typography>
            <Typography variant="h5" fontWeight={700}>Payment Receipt</Typography>
          </Box>
          <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Typography color="text.secondary">{r.patientName}</Typography>
            {r.payerName && <Meta>From: {r.payerName}</Meta>}
            {methodLine && <Meta>{methodLine}</Meta>}
            {r.depositDate && <Meta>Posted {formatDate(r.depositDate)}</Meta>}
            {r.encounterDate && <Meta>Visit: {formatDate(r.encounterDate)}</Meta>}

            <Box sx={{ mt: 2.5 }}>
              {(r.lines || []).map((line, index) => (
                <Row
                  key={index}
                  label={line.code || 'Payment'}
                  sub={line.memo || undefined}
                  amount={money(line.payAmount)}
                />
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, mt: 0.5, borderTop: '2px solid #172033' }}>
                <Typography fontWeight={800} fontSize={17}>Total Paid</Typography>
                <Typography fontWeight={800} fontSize={17} color="primary">{money(r.totalPaid)}</Typography>
              </Box>
              {Number(r.totalAdjusted) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                  <Typography fontWeight={600} fontSize={14}>Total Adjusted</Typography>
                  <Typography fontWeight={700} fontSize={14}>{money(r.totalAdjusted)}</Typography>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
              Thank you for your payment.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  const inv = doc.invoice;
  const practiceLine = [inv.practiceCity, inv.practiceState].filter(Boolean).join(', ');
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', px: 2, py: { xs: 3, sm: 6 } }}>
      <Paper elevation={0} sx={{ maxWidth: 560, mx: 'auto', borderRadius: 3, overflow: 'hidden', border: '1px solid #dbe5f1' }}>
        <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: 3, py: 2.5 }}>
          <Typography variant="overline" sx={{ opacity: 0.85 }}>{inv.practiceName}</Typography>
          <Typography variant="h5" fontWeight={700}>Encounter Statement</Typography>
        </Box>
        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          {inv.practiceStreet && <Meta>{inv.practiceStreet}</Meta>}
          {practiceLine && <Meta>{practiceLine}{inv.practiceZip ? ` ${inv.practiceZip}` : ''}</Meta>}
          {inv.practicePhone && <Meta>{inv.practicePhone}</Meta>}
          <Divider sx={{ my: 1.5 }} />
          <Typography color="text.secondary">{inv.patientName}</Typography>
          {inv.encounterDate && <Meta>Visit: {formatDate(inv.encounterDate)}{inv.visitType ? ` · ${inv.visitType}` : ''}</Meta>}
          {inv.providerName && <Meta>Provider: {inv.providerName}</Meta>}
          {inv.facilityName && <Meta>Facility: {inv.facilityName}</Meta>}
          {inv.payerName && <Meta>Third Party Payer: {inv.payerName}</Meta>}

          {(inv.diagnosisLines || []).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Diagnosis</Typography>
              {inv.diagnosisLines.map((line, index) => (
                <Row key={index} label={line.code} sub={line.description || undefined} />
              ))}
            </>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Charges</Typography>
          {(inv.chargeLines || []).map((line, index) => (
            <Row
              key={index}
              label={`${line.code}${line.modifier ? `-${line.modifier}` : ''}`}
              sub={[line.description, line.units > 1 ? `${line.units} units` : null].filter(Boolean).join(' · ') || undefined}
              amount={money(line.fee)}
            />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
            <Typography fontWeight={700} fontSize={14}>Total Charges</Typography>
            <Typography fontWeight={700} fontSize={14}>{money(inv.totalCharge)}</Typography>
          </Box>

          {(inv.paymentSessions || []).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Payments &amp; Adjustments</Typography>
              {inv.paymentSessions.map((s, index) => (
                <Row
                  key={index}
                  label={s.payerName}
                  sub={[paymentMethodIcon(s.method), s.depositDate ? formatDate(s.depositDate) : null].filter(Boolean).join(' · ') || undefined}
                  amount={`-${money(s.paid + s.adjusted)}`}
                />
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                <Typography fontWeight={700} fontSize={14}>Total Payments &amp; Adjustments</Typography>
                <Typography fontWeight={700} fontSize={14}>-{money(inv.totalPaid + inv.totalAdjusted)}</Typography>
              </Box>
            </>
          )}

          <Paper variant="outlined" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, p: 2, borderRadius: 2, bgcolor: '#f4f7fb' }}>
            <Typography fontWeight={800} fontSize={16}>Balance Due</Typography>
            <Typography fontWeight={800} fontSize={20} color="primary">{money(inv.balance)}</Typography>
          </Paper>

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
            Questions about this statement? Please contact {inv.practiceName}.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
